import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Invoices from "@/pages/Invoices";
import FreightInvoices from "@/pages/FreightInvoices";
import BukkuProducts from "@/pages/BukkuProducts";
import BukkuLogs from "@/pages/BukkuLogs";
import ShippingRates from "@/pages/ShippingRates";
import BillingCompanies from "@/pages/BillingCompanies";
import { FileText, Receipt, RefreshCcw, Settings, DollarSign, Building, CloudUpload } from "lucide-react";

const allow = (item, can) => item.perm ? can(item.perm) : item.anyPerm ? item.anyPerm.some(can) : true;

function Counter({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
  };
  return (
    <div data-testid={testid} className={`rounded-lg border px-4 py-2 ${tones[tone]}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-bold leading-tight">{value ?? "—"}</div>
    </div>
  );
}

function SubTabs({ tabs, value, onChange }) {
  return (
    <div className="flex gap-1 mb-4">
      {tabs.map(t => {
        const Icon = t.icon;
        return (
          <button key={t.k} data-testid={t.testid} onClick={() => onChange(t.k)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition ${
              value === t.k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}>
            {Icon && <Icon className="w-3.5 h-3.5" />}{t.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Finance() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();

  const MAIN = useMemo(() => [
    { k: "invoices", label: "Purchase Invoices", icon: FileText, testid: "fin-tab-invoices", perm: "invoice:view" },
    { k: "freight", label: "Freight Invoices", icon: Receipt, testid: "fin-tab-freight", perm: "freight:view" },
    { k: "bukku", label: "Bukku", icon: RefreshCcw, testid: "fin-tab-bukku", anyPerm: ["bukku:view", "bukku:logs"] },
    { k: "setup", label: "Setup", icon: Settings, testid: "fin-tab-setup", anyPerm: ["system:shipping_rates", "system:billing_companies"] },
  ].filter(t => allow(t, can)), [can]);

  const qTab = params.get("tab");
  const initial = MAIN.find(t => t.k === qTab)?.k || MAIN[0]?.k;
  const [tab, setTab] = useState(initial);
  const active = MAIN.find(t => t.k === tab) ? tab : MAIN[0]?.k;
  const go = (k) => { setTab(k); setParams(prev => { const p = new URLSearchParams(prev); p.set("tab", k); return p; }, { replace: true }); };
  useEffect(() => { if (qTab && MAIN.find(t => t.k === qTab)) setTab(qTab); }, [qTab, MAIN]);

  // Bukku sub-tabs
  const bukkuSubs = [
    { k: "product-sync", label: "Product Sync", icon: RefreshCcw, testid: "fin-bukku-sync", perm: "bukku:view" },
    { k: "sync-logs", label: "Sync Logs", icon: CloudUpload, testid: "fin-bukku-logs", perm: "bukku:logs" },
  ].filter(t => allow(t, can));
  const [bukkuSub, setBukkuSub] = useState(bukkuSubs[0]?.k);

  // Setup sub-tabs
  const setupSubs = [
    { k: "rates", label: "Shipping Rates", icon: DollarSign, testid: "fin-setup-rates", perm: "system:shipping_rates" },
    { k: "billing", label: "Billing Companies", icon: Building, testid: "fin-setup-billing", perm: "system:billing_companies" },
  ].filter(t => allow(t, can));
  const [setupSub, setSetupSub] = useState(setupSubs[0]?.k);

  // Summary counters
  const [counts, setCounts] = useState({ invoices: null, invoicesNeedAction: null, freight: null, bukkuPending: null });
  useEffect(() => {
    (async () => {
      const next = {};
      if (can("invoice:view")) {
        try {
          const r = await api.get("/invoices");
          next.invoices = r.data.length;
          next.invoicesNeedAction = r.data.filter(i => i.final_amount_rm == null || !i.bukku_synced).length;
        } catch { /* permission/API */ }
      }
      if (can("freight:view")) {
        try { const r = await api.get("/freight-invoices"); next.freight = r.data.length; } catch { /* */ }
      }
      if (can("bukku:view_status") || can("bukku:view")) {
        try { const r = await api.get("/bukku/products/status"); next.bukkuPending = r.data.missing; } catch { /* */ }
      }
      setCounts(c => ({ ...c, ...next }));
    })();
  }, [can]);

  return (
    <div data-testid="finance-page">
      <header className="mb-4">
        <div className="caption">Financial workspace</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Purchasing &amp; Finance</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Purchase &amp; freight invoices, Bukku accounting sync, and setup — the costing/financial side of the ledger.
        </p>
      </header>

      <div className="flex gap-3 mb-5 flex-wrap">
        {can("invoice:view") && <Counter testid="fin-count-invoices" label="Purchase Invoices" value={counts.invoices} tone="sky" />}
        {can("freight:view") && <Counter testid="fin-count-freight" label="Freight Invoices" value={counts.freight} tone="slate" />}
        {can("invoice:view") && <Counter testid="fin-count-needs-action" label="Needs Action" value={counts.invoicesNeedAction} tone="amber" />}
        {(can("bukku:view") || can("bukku:view_status")) && <Counter testid="fin-count-bukku-pending" label="Bukku Pending" value={counts.bukkuPending} tone="violet" />}
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200 flex-wrap">
        {MAIN.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.k} data-testid={t.testid} onClick={() => go(t.k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition inline-flex items-center gap-2 ${
                active === t.k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {active === "invoices" && <Invoices />}
      {active === "freight" && <FreightInvoices />}
      {active === "bukku" && (
        <div>
          <SubTabs tabs={bukkuSubs} value={bukkuSub} onChange={setBukkuSub} />
          {bukkuSub === "product-sync" && <BukkuProducts />}
          {bukkuSub === "sync-logs" && <BukkuLogs />}
        </div>
      )}
      {active === "setup" && (
        <div>
          <SubTabs tabs={setupSubs} value={setupSub} onChange={setSetupSub} />
          {setupSub === "rates" && <ShippingRates />}
          {setupSub === "billing" && <BillingCompanies />}
        </div>
      )}
    </div>
  );
}
